import {
  FC,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import Modal from "../modal/Modal";
import Button from "../button/Button";
import ResponseChip from "../response-chip/ResponseChip";
import MoneyInput from "../form-item/MoneyInput";
import {
  useCreateDepositIntentMutation,
  useGetDepositQuoteQuery,
  type DepositQuote,
} from "../../app/services/deposit";
import { invalidateOfferAccountCaches } from "../../app/services/offerAccountCache";
import {
  useGetCurrentUserQuery,
  // @ts-expect-error -- 型宣言なしの JS モジュール
} from "../../app/accounts/authuser";
import { formatYen } from "../../utils/money";

// BE `settings.MIN_AMOUNT` / `MAX_AMOUNT` と一致させる
const MIN_CHARGE_AMOUNT = 100;
const MAX_CHARGE_AMOUNT = 99_999_999;

// 旧 Django のプロジェクト作成（artist_create_product）を踏襲したプリセット。
// クリックで現在の入力額に加算する。
const PRESET_AMOUNTS = [
  15_000, 30_000, 100_000, 300_000, 1_000_000, 3_000_000,
];

// quote API を叩きすぎないための入力デバウンス
const QUOTE_DEBOUNCE_MS = 400;

// 決済成功後、Webhook の残高反映を待つポーリング設定
const BALANCE_POLL_INTERVAL_MS = 2000;
const BALANCE_POLL_MAX_ATTEMPTS = 10;

// ── styled-components ──

const PresetRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

// 内訳枠は進行中モーダル（TransactionsModal）の SummaryBox とデザインを揃える
const SummaryBox = styled.div`
  padding: 16px 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 4px;
  background: var(--soremo-background);
`;

const SummaryRow = styled.div.attrs({
  className: "u-row-between u-w100",
})`
  padding: 2px 0;
`;

const SummaryDivider = styled.hr`
  border: none;
  border-top: 1px solid var(--soremo-border);
  margin: 4px 0;
`;

const SummaryLabel = styled.span.attrs({ className: "bodytext-13" })``;

const SummaryValue = styled.span.attrs({ className: "bodytext-13" })``;

const TotalLabel = styled.span.attrs({ className: "heading-16" })``;

const TotalValue = styled.span.attrs({ className: "heading-18" })``;

const HintText = styled.div.attrs({
  className: "bodytext-11 u-line-height-150",
})`
  color: var(--soremo-light-gray);
`;

const ErrorText = styled.div.attrs({
  className: "bodytext-13 u-line-height-150",
})`
  color: var(--soremo-green);
`;

const DoneText = styled.div.attrs({
  className: "bodytext-13 u-line-height-150",
})`
  color: var(--soremo-gray);
`;

const PayFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

// ── 決済フォーム（Elements 配下でのみ使用可能） ──

interface ChargePaymentFormProps {
  totalLabel: string;
  onBack: () => void;
  onSuccess: () => void;
}

const ChargePaymentForm: FC<ChargePaymentFormProps> = ({
  totalLabel,
  onBack,
  onSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setIsProcessing(true);
    setErrorMessage(null);

    // 3D セキュア等が必要な場合のみリダイレクトされる（通常はインライン完結）。
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });

    if (error) {
      setErrorMessage(
        error.message ?? "決済に失敗しました。時間をおいて再度お試しください。",
      );
      setIsProcessing(false);
      return;
    }

    if (
      paymentIntent &&
      (paymentIntent.status === "succeeded" ||
        paymentIntent.status === "processing")
    ) {
      onSuccess();
      return;
    }

    setErrorMessage("決済が完了しませんでした。再度お試しください。");
    setIsProcessing(false);
  };

  return (
    <>
      <PaymentElement />
      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
      <PayFooter>
        <Button
          size="small"
          theme="tertiary"
          variant="text"
          width="fit"
          onClick={onBack}
          disabled={isProcessing}
        >
          戻る
        </Button>
        <Button
          size="small"
          theme="primary"
          variant="filled"
          onClick={handlePay}
          disabled={!stripe || !elements || isProcessing}
        >
          {isProcessing ? "決済中..." : `${totalLabel} を支払う`}
        </Button>
      </PayFooter>
    </>
  );
};

// ── メインコンポーネント ──

type ChargeStep = "amount" | "payment" | "done";

export interface ChargeModalProps {
  onClose: () => void;
}

/**
 * クレジットカードによる残高チャージのモーダル。
 *
 * 1. 金額選択: チャージ額（税抜）を直接入力またはプリセットボタンで加算。
 *    quote API の内訳（サービス利用料 / 消費税 / お支払い合計）をリアルタイム表示。
 * 2. 決済: Stripe Payment Element で `stripe.confirmPayment`。
 * 3. 完了: Webhook による `balance_free` 反映をポーリングで待って残高表示を更新。
 *
 * 支払い額 = チャージ額 + サービス利用料（マイレージ料率）+ 消費税 10%。
 * Stripe 決済手数料・振込手数料の上乗せはない。
 */
const ChargeModal: FC<ChargeModalProps> = ({ onClose }) => {
  const dispatch = useDispatch();

  const [step, setStep] = useState<ChargeStep>("amount");
  const [amountInput, setAmountInput] = useState("");
  const [debouncedAmount, setDebouncedAmount] = useState(0);
  const [intentError, setIntentError] = useState<string | null>(null);

  // 決済ステップに渡す intent 情報（作成後のみ非 null）
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [confirmedQuote, setConfirmedQuote] = useState<DepositQuote | null>(
    null,
  );
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);

  const amount = Number(amountInput || 0);
  const isAmountValid =
    Number.isInteger(amount) &&
    amount >= MIN_CHARGE_AMOUNT &&
    amount <= MAX_CHARGE_AMOUNT;

  // 入力デバウンス → quote 取得
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAmount(isAmountValid ? amount : 0);
    }, QUOTE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [amount, isAmountValid]);

  const { data: quoteData, isFetching: isQuoteFetching } =
    useGetDepositQuoteQuery(
      { amount: debouncedAmount },
      { skip: debouncedAmount < MIN_CHARGE_AMOUNT },
    );
  const quote =
    debouncedAmount >= MIN_CHARGE_AMOUNT ? quoteData?.data?.quote : undefined;
  const isQuoteReady =
    !!quote && !isQuoteFetching && Number(quote.charge_amount) === amount;

  const [createDepositIntent, { isLoading: isCreatingIntent }] =
    useCreateDepositIntentMutation();

  // 残高反映の確認用。モーダルを開いた時点の balance_free を基準にする。
  const { data: currentUser } = useGetCurrentUserQuery();
  const currentFree = Number(currentUser?.data?.balance?.free ?? 0);
  const initialFreeRef = useRef<number | null>(null);
  useEffect(() => {
    if (initialFreeRef.current === null && currentUser?.data) {
      initialFreeRef.current = currentFree;
    }
  }, [currentUser, currentFree]);

  const chargedAmount = Number(confirmedQuote?.charge_amount ?? 0);
  const isBalanceReflected =
    initialFreeRef.current !== null &&
    chargedAmount > 0 &&
    currentFree >= initialFreeRef.current + chargedAmount;

  // 完了ステップ: Webhook の反映を待つポーリング（/api/me を再取得）
  useEffect(() => {
    if (step !== "done" || isBalanceReflected) return;
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      invalidateOfferAccountCaches(dispatch);
      if (attempts >= BALANCE_POLL_MAX_ATTEMPTS) {
        clearInterval(timer);
      }
    }, BALANCE_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [step, isBalanceReflected, dispatch]);

  const handlePreset = (preset: number) => {
    const next = Math.min(amount + preset, MAX_CHARGE_AMOUNT);
    setAmountInput(String(next));
  };

  const handleProceedToPayment = async () => {
    if (!isQuoteReady || !quote) return;
    setIntentError(null);
    try {
      const result = await createDepositIntent({ amount }).unwrap();
      const { client_secret, publishable_key, quote: intentQuote } =
        result.data;
      if (!client_secret || !publishable_key) {
        setIntentError(
          "決済の初期化に失敗しました。時間をおいて再度お試しください。",
        );
        return;
      }
      setClientSecret(client_secret);
      setConfirmedQuote(intentQuote);
      setStripePromise(loadStripe(publishable_key));
      setStep("payment");
    } catch {
      setIntentError(
        "決済の準備に失敗しました。時間をおいて再度お試しください。",
      );
    }
  };

  const totalLabel = formatYen(Number(confirmedQuote?.total ?? 0)) ?? "¥0";

  // ── 完了 ──
  if (step === "done") {
    return (
      <Modal
        title="チャージが完了しました"
        onClose={onClose}
        size="default"
        primaryAction={{ label: "閉じる", onClick: onClose }}
      >
        <DoneText>
          {formatYen(chargedAmount)}
          （税抜）をチャージしました。お支払い額は {totalLabel} です。
          <br />
          {isBalanceReflected
            ? "残高に反映されました。"
            : "残高への反映には少し時間がかかる場合があります。"}
        </DoneText>
      </Modal>
    );
  }

  // ── 決済 ──
  if (step === "payment" && clientSecret && stripePromise) {
    return (
      <Modal
        title="お支払い"
        description={`お支払い額 ${totalLabel}（チャージ額 + サービス利用料 + 消費税）`}
        onClose={onClose}
        size="default"
      >
        <Elements
          stripe={stripePromise}
          options={{ clientSecret, locale: "ja" }}
        >
          <ChargePaymentForm
            totalLabel={totalLabel}
            onBack={() => {
              // intent は作り直す（金額が変わる可能性があるため）
              setClientSecret(null);
              setConfirmedQuote(null);
              setStep("amount");
            }}
            onSuccess={() => setStep("done")}
          />
        </Elements>
      </Modal>
    );
  }

  // ── 金額選択 ──
  return (
    <Modal
      title="残高を増やす"
      description="チャージする金額（税抜）を入力してください。"
      onClose={onClose}
      size="default"
      primaryAction={{
        label: "決済へ進む",
        onClick: handleProceedToPayment,
        rightIcon: "chevron_right",
        loading: isCreatingIntent,
        disabled: !isQuoteReady || isCreatingIntent,
      }}
      secondaryAction={{
        label: "キャンセル",
        onClick: onClose,
        theme: "secondary",
        disabled: isCreatingIntent,
      }}
    >
      <MoneyInput
        label="チャージ額（税抜）"
        placeholder="0"
        value={amountInput}
        onChange={(e) => setAmountInput(e.target.value)}
      />

      {/* プリセット加算チップ（共通 ResponseChip でデザインを統一） */}
      <PresetRow>
        {PRESET_AMOUNTS.map((preset) => (
          <ResponseChip key={preset} onClick={() => handlePreset(preset)}>
            +{preset.toLocaleString("ja-JP")}
          </ResponseChip>
        ))}
      </PresetRow>

      {quote && (
        <SummaryBox>
          <SummaryRow>
            <SummaryLabel>チャージ額（税抜）</SummaryLabel>
            <SummaryValue>
              {formatYen(Number(quote.charge_amount))}
            </SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>
              サービス利用料（{quote.usage_fee_rate}%）
            </SummaryLabel>
            <SummaryValue>{formatYen(Number(quote.usage_fee))}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>消費税（10%）</SummaryLabel>
            <SummaryValue>{formatYen(Number(quote.tax))}</SummaryValue>
          </SummaryRow>
          <SummaryDivider />
          <SummaryRow>
            <TotalLabel>お支払い合計</TotalLabel>
            <TotalValue>{formatYen(Number(quote.total))}</TotalValue>
          </SummaryRow>
        </SummaryBox>
      )}

      {intentError && <ErrorText>{intentError}</ErrorText>}

      <HintText>
        チャージした残高（税抜）がオファーに使える残高へ加算されます。
        {MIN_CHARGE_AMOUNT.toLocaleString("ja-JP")}円から
        {MAX_CHARGE_AMOUNT.toLocaleString("ja-JP")}円までチャージできます。
      </HintText>
    </Modal>
  );
};

export default ChargeModal;
